import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { useReferentBrandingColors } from '@/hooks/useReferentBrandingColors';

interface CriticalQuestionsData {
  wouldRecommend: boolean | null;
  wouldRehire: boolean | null;
  hasIncidents: boolean | null;
  incidentsDescription?: string;
}

interface BrandingSettings {
  enabled: boolean;
  logo_url?: string;
  primary_color?: string;
}

interface CriticalQuestionsStepProps {
  updateFormData: (data: any) => void;
  formData?: any;
  branding?: BrandingSettings | null;
}

export const CriticalQuestionsStep = ({ updateFormData, formData, branding }: CriticalQuestionsStepProps) => {
  const colors = useReferentBrandingColors(branding);
  const [answers, setAnswers] = useState<CriticalQuestionsData>({
    wouldRecommend: formData?.criticalQuestions?.wouldRecommend || null,
    wouldRehire: formData?.criticalQuestions?.wouldRehire || null,
    hasIncidents: formData?.criticalQuestions?.hasIncidents || null,
    incidentsDescription: formData?.criticalQuestions?.incidentsDescription || '',
  });

  // Mettre à jour formData quand les réponses changent
  useEffect(() => {
    updateFormData({ criticalQuestions: answers });
  }, [answers, updateFormData]);

  const updateAnswer = (key: keyof CriticalQuestionsData, value: boolean | string) => {
    setAnswers(prev => ({ 
      ...prev, 
      [key]: value,
      // Reset incidents description if hasIncidents becomes false
      ...(key === 'hasIncidents' && value === false ? { incidentsDescription: '' } : {})
    }));
  };

  return (
    <div className="space-y-6 font-['Bricolage_Grotesque']">
      {/* Description sans titre (titre géré par le container) */}
      <div className="text-center">
        <p className="text-gray-600">
          Veuillez répondre à ces 3 questions obligatoires.
        </p>
      </div>

      <Card>
        <CardContent className="p-6 space-y-8">
          {/* Question 1: Would recommend */}
          <div className="space-y-3">
            <Label className="text-base font-medium">
              1. Recommanderiez-vous cette personne pour un poste similaire ? *
            </Label>
            <RadioGroup
              value={answers.wouldRecommend?.toString() || ''}
              onValueChange={(value) => updateAnswer('wouldRecommend', value === 'true')}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem 
                  value="true" 
                  id="recommend-yes"
                  style={{ '--radio-color': colors.primary } as any}
                />
                <Label htmlFor="recommend-yes">Oui</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem 
                  value="false" 
                  id="recommend-no"
                  style={{ '--radio-color': colors.primary } as any}
                />
                <Label htmlFor="recommend-no">Non</Label>
              </div>
            </RadioGroup>

          </div>

          {/* Question 2: Would rehire */}
          <div className="space-y-3">
            <Label className="text-base font-medium">
              2. Accepteriez-vous de retravailler avec cette personne ? *
            </Label>
            <RadioGroup
              value={answers.wouldRehire?.toString() || ''}
              onValueChange={(value) => updateAnswer('wouldRehire', value === 'true')}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem 
                  value="true" 
                  id="rehire-yes"
                  style={{ '--radio-color': colors.primary } as any}
                />
                <Label htmlFor="rehire-yes">Oui</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem 
                  value="false" 
                  id="rehire-no"
                  style={{ '--radio-color': colors.primary } as any}
                />
                <Label htmlFor="rehire-no">Non</Label>
              </div>
            </RadioGroup>

          </div>

          {/* Question 3: Has incidents */}
          <div className="space-y-3">
            <Label className="text-base font-medium">
              3. Avez-vous eu des incidents ou des problèmes professionnels avec cette personne ? *
            </Label>
            <RadioGroup
              value={answers.hasIncidents?.toString() || ''}
              onValueChange={(value) => updateAnswer('hasIncidents', value === 'true')}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem 
                  value="true" 
                  id="incidents-yes"
                  style={{ '--radio-color': colors.primary } as any}
                />
                <Label htmlFor="incidents-yes">Oui</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem 
                  value="false" 
                  id="incidents-no"
                  style={{ '--radio-color': colors.primary } as any}
                />
                <Label htmlFor="incidents-no">Non</Label>
              </div>
            </RadioGroup>


            {/* Conditional description field */}
            {answers.hasIncidents === true && (
              <div className="space-y-2 mt-4">
                <Label htmlFor="incidents-description" className="text-sm font-medium">
                  Veuillez décrire ces incidents :
                </Label>
                <Textarea
                  id="incidents-description"
                  placeholder="Décrivez les incidents ou problèmes rencontrés..."
                  value={answers.incidentsDescription || ''}
                  onChange={(e) => updateAnswer('incidentsDescription', e.target.value)}
                  className="min-h-[100px]"
                />

              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};